import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';

// ─── localStorage session helpers ────────────────────────────────────────────

// ─── Shared mobile-friendly input styles ─────────────────────────────────────
const mobileInput = {
  width: '100%', background: 'var(--bg-primary)', border: '1px solid var(--border)',
  borderRadius: 6, color: 'var(--text-primary)', padding: '14px 16px',
  fontFamily: 'var(--font-body)', fontSize: 16, lineHeight: 1.4,
  boxSizing: 'border-box', WebkitAppearance: 'none', appearance: 'none',
};
const focusStyle = { borderColor: 'var(--accent)', outline: 'none' };

function MField({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function MSelect({ value, onChange, children, ...rest }) {
  const [f, setF] = useState(false);
  return (
    <select value={value} onChange={onChange} onFocus={() => setF(true)} onBlur={() => setF(false)}
      style={{ ...mobileInput, ...(f ? focusStyle : {}), cursor: 'pointer' }} {...rest}>
      {children}
    </select>
  );
}

function MInput({ value, onChange, type = 'text', ...rest }) {
  const [f, setF] = useState(false);
  return (
    <input type={type} value={value} onChange={onChange}
      onFocus={() => setF(true)} onBlur={() => setF(false)}
      style={{ ...mobileInput, ...(f ? focusStyle : {}) }} {...rest} />
  );
}

// ─── Reusable toggle switch ───────────────────────────────────────────────────
function Toggle({ label, subLabel, value, onChange, color = 'var(--info)' }) {
  return (
    <div
      onClick={() => onChange(!value)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
        background: value ? `rgba(88,166,255,0.08)` : 'var(--bg-card)',
        border: value ? `1px solid rgba(88,166,255,0.3)` : '1px solid var(--border)',
        borderRadius: 6, padding: '14px 16px', marginBottom: 16,
        transition: 'background 0.15s, border-color 0.15s',
      }}
    >
      <div style={{
        width: 44, height: 24, borderRadius: 12, flexShrink: 0,
        background: value ? color : 'var(--border)',
        position: 'relative', transition: 'background 0.2s',
      }}>
        <div style={{
          position: 'absolute', top: 3, left: value ? 23 : 3,
          width: 18, height: 18, borderRadius: '50%',
          background: value ? '#fff' : 'var(--text-muted)',
          transition: 'left 0.2s',
        }} />
      </div>
      <div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: value ? color : 'var(--text-primary)' }}>
          {label}
        </div>
        {subLabel && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{subLabel}</div>}
      </div>
    </div>
  );
}

// ─── Tab button ───────────────────────────────────────────────────────────────
function TabBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '10px 18px', borderRadius: 4, cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
      border:      active ? '1px solid var(--accent)' : '1px solid var(--border)',
      background:  active ? 'var(--accent-glow)'      : 'var(--bg-card)',
      color:       active ? 'var(--accent)'            : 'var(--text-secondary)',
      fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase',
    }}>
      {children}
    </button>
  );
}

// ─── Mode toggle (Clock / Manual) ────────────────────────────────────────────
function ModeBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      flex: 1, padding: '12px 8px', borderRadius: 4, cursor: 'pointer', transition: 'all 0.15s',
      border:     active ? '2px solid var(--accent)' : '1px solid var(--border)',
      background: active ? 'var(--accent-glow)'      : 'var(--bg-secondary)',
      color:      active ? 'var(--accent)'            : 'var(--text-secondary)',
      fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
    }}>
      {children}
    </button>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ msg, type, onDismiss }) {
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [msg, onDismiss]);
  if (!msg) return null;
  const ok = type === 'success';
  return (
    <div style={{
      position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
      zIndex: 2000, maxWidth: 420, width: 'calc(100% - 32px)',
      background: ok ? 'rgba(63,185,80,0.15)' : 'rgba(248,81,73,0.15)',
      border: `1px solid ${ok ? 'rgba(63,185,80,0.4)' : 'rgba(248,81,73,0.4)'}`,
      borderRadius: 8, padding: '14px 18px',
      color: ok ? 'var(--success)' : 'var(--danger)',
      fontFamily: 'var(--font-mono)', fontSize: 13,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
    }}>
      <span>{ok ? '✓ ' : '✗ '}{msg}</span>
      <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 18, lineHeight: 1 }}>×</button>
    </div>
  );
}

// ─── Project info pill ────────────────────────────────────────────────────────
function ProjectPill({ project }) {
  if (!project) return null;
  return (
    <div style={{
      background: 'var(--bg-primary)', border: '1px solid var(--accent)',
      borderRadius: 6, padding: '10px 14px', marginBottom: 16,
      display: 'flex', gap: 20, flexWrap: 'wrap',
    }}>
      <div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 2 }}>Job</div>
        <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)', fontSize: 15, fontWeight: 700 }}>{project.job_number}</div>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 2 }}>Customer</div>
        <div style={{ fontSize: 14 }}>{project.customer_name || '—'}</div>
      </div>
      <div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 2 }}>Status</div>
        <span className={`badge badge-${project.status}`} style={{ fontSize: 10 }}>
          {project.status?.replace(/_/g, ' ')}
        </span>
      </div>
    </div>
  );
}

// ─── Elapsed time helper ──────────────────────────────────────────────────────
function elapsedStr(startedAt, now) {
  const ms   = now - new Date(startedAt).getTime();
  const mins = Math.floor(ms / 60000);
  const hrs  = Math.floor(mins / 60);
  const rem  = mins % 60;
  if (hrs > 0) return `${hrs}h ${rem}m`;
  return `${rem}m`;
}
function elapsedHours(startedAt) {
  const ms = Date.now() - new Date(startedAt).getTime();
  return Math.max(0.02, Math.round(ms / 3600000 * 100) / 100);
}

// ─── Clock On/Off mode (uses DB /clock-sessions API) ────────────────────────
function ClockMode({ projects, workCentres, staff, onLogged }) {
  const [form, setForm]     = useState({ staff_id: '', work_centre_id: '', project_id: '', non_productive: false, description: '' });
  const [sessions, setSessions] = useState([]);
  const [now, setNow]       = useState(Date.now());
  const [toast, setToast]   = useState(null);
  const [saving, setSaving] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const loadSessions = useCallback(() => {
    api.get('/clock-sessions').then(r => setSessions(r.data.value || [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadSessions();
    const tick = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(tick);
  }, [loadSessions]);

  const clockOn = async () => {
    if (!form.staff_id || !form.work_centre_id) {
      setToast({ msg: 'Select your name and work centre first.', type: 'error' }); return;
    }
    if (!form.non_productive && !form.project_id) {
      setToast({ msg: 'Select a project, or enable Non-Productive.', type: 'error' }); return;
    }
    try {
      const resp = await api.post('/clock-sessions', {
        staff_id:       parseInt(form.staff_id),
        work_centre_id: parseInt(form.work_centre_id),
        project_id:     form.non_productive ? null : parseInt(form.project_id),
        non_productive: form.non_productive,
        description:    form.description || null,
      });
      setSessions(prev => [...prev, resp.data]);
      const time = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      setToast({ msg: `${resp.data.staff_name} clocked on at ${time}`, type: 'success' });
      set('project_id', ''); set('description', '');
    } catch (e) {
      setToast({ msg: e.response?.data?.error || e.message, type: 'error' });
    }
  };

  const clockOff = async (session) => {
    setSaving(session.id);
    try {
      const resp = await api.delete(`/clock-sessions/${session.id}`);
      setSessions(prev => prev.filter(s => s.id !== session.id));
      setToast({ msg: `${resp.data.staff_name} clocked off — ${resp.data.hours_logged.toFixed(2)}h logged`, type: 'success' });
      onLogged();
    } catch (e) {
      setToast({ msg: e.response?.data?.error || e.message, type: 'error' });
    }
    setSaving(null);
  };

  const selectedProject = !form.non_productive && form.project_id
    ? projects.find(p => p.id === parseInt(form.project_id))
    : null;

  return (
    <div style={{ maxWidth: 560 }}>
      <Toast msg={toast?.msg} type={toast?.type} onDismiss={() => setToast(null)} />

      <Toggle
        label="Non-Productive Time"
        subLabel="Clean down, general shop work, training, etc."
        value={form.non_productive}
        onChange={v => { set('non_productive', v); set('project_id', ''); }}
      />

      <MField label="Who are you? *">
        <MSelect value={form.staff_id} onChange={e => set('staff_id', e.target.value)}>
          <option value="">Select name...</option>
          {staff.map(s => <option key={s.id} value={s.id}>{s.name}{s.role ? ` — ${s.role}` : ''}</option>)}
        </MSelect>
      </MField>

      <MField label="Work Centre *">
        <MSelect value={form.work_centre_id} onChange={e => set('work_centre_id', e.target.value)}>
          <option value="">Select work centre...</option>
          {workCentres.map(wc => <option key={wc.id} value={wc.id}>{wc.name}</option>)}
        </MSelect>
      </MField>

      {form.non_productive ? (
        <MField label="Activity description">
          <MInput value={form.description} onChange={e => set('description', e.target.value)}
            placeholder="e.g. Clean down, general shop work..." />
        </MField>
      ) : (
        <>
          <MField label="Project *">
            <MSelect value={form.project_id} onChange={e => set('project_id', e.target.value)}>
              <option value="">Select project...</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.job_number} — {p.title}</option>)}
            </MSelect>
          </MField>
          <ProjectPill project={selectedProject} />
        </>
      )}

      <button onClick={clockOn} style={{
        width: '100%', padding: '18px 24px', marginTop: 4, border: 'none', borderRadius: 6,
        background: 'var(--success)', color: '#fff',
        fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, letterSpacing: 2,
        textTransform: 'uppercase', cursor: 'pointer',
      }}>
        Clock On
      </button>

      {sessions.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>
            Currently Clocked On ({sessions.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {sessions.map(s => (
              <div key={s.id} style={{
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '16px',
                display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
                    {s.staff_name}
                  </div>
                  {s.non_productive ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ background: 'rgba(88,166,255,0.12)', color: 'var(--info)', border: '1px solid rgba(88,166,255,0.3)', borderRadius: 3, padding: '1px 7px', fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase' }}>NON-PROD</span>
                      {s.description && <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{s.description}</span>}
                    </div>
                  ) : (
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)', marginBottom: 4 }}>
                      {s.job_number} <span style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}>{s.project_title}</span>
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {s.work_centre_name} · clocked on {elapsedStr(s.started_at, now)} ago
                  </div>
                </div>
                <button onClick={() => clockOff(s)} disabled={saving === s.id} style={{
                  padding: '14px 18px', border: 'none', borderRadius: 6, cursor: 'pointer',
                  background: saving === s.id ? 'var(--bg-hover)' : 'var(--danger)',
                  color: '#fff', fontFamily: 'var(--font-display)', fontSize: 14,
                  fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
                  minWidth: 90, opacity: saving === s.id ? 0.6 : 1,
                }}>
                  {saving === s.id ? '...' : 'Clock Off'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Manual entry mode ────────────────────────────────────────────────────────
function ManualMode({ projects, workCentres, staff, onLogged }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    project_id: '', work_centre_id: '', staff_id: '',
    log_date: today, hours_decimal: '', pct_complete: '',
    is_batch: false, non_productive: false, notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [toast,  setToast]  = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.work_centre_id || !form.staff_id || !form.hours_decimal) {
      setToast({ msg: 'Work centre, staff and hours are required.', type: 'error' }); return;
    }
    if (!form.non_productive && !form.project_id) {
      setToast({ msg: 'Select a project, or enable Non-Productive.', type: 'error' }); return;
    }
    if (parseFloat(form.hours_decimal) <= 0) {
      setToast({ msg: 'Hours must be greater than 0.', type: 'error' }); return;
    }
    setSaving(true);
    try {
      await api.post('/production-logs', {
        project_id:     form.non_productive ? null : parseInt(form.project_id),
        work_centre_id: parseInt(form.work_centre_id),
        staff_id:       parseInt(form.staff_id),
        log_date:       form.log_date,
        hours_decimal:  parseFloat(form.hours_decimal),
        pct_complete:   form.pct_complete !== '' ? parseFloat(form.pct_complete) : null,
        is_batch:       form.is_batch,
        non_productive: form.non_productive,
        notes:          form.notes || null,
      });
      setToast({ msg: 'Entry logged successfully!', type: 'success' });
      setForm(p => ({ ...p, hours_decimal: '', pct_complete: '', notes: '', is_batch: false, project_id: '' }));
      onLogged();
    } catch (e) {
      setToast({ msg: e.response?.data?.error || e.message, type: 'error' });
    }
    setSaving(false);
  };

  const selectedProject = !form.non_productive && form.project_id
    ? projects.find(p => p.id === parseInt(form.project_id))
    : null;

  return (
    <div style={{ maxWidth: 560 }}>
      <Toast msg={toast?.msg} type={toast?.type} onDismiss={() => setToast(null)} />

      {/* Non-Productive toggle */}
      <Toggle
        label="Non-Productive Time"
        subLabel="Clean down, general shop work, training, etc."
        value={form.non_productive}
        onChange={v => { set('non_productive', v); set('project_id', ''); }}
      />

      {/* Project or activity field */}
      {form.non_productive ? (
        <MField label="Activity description">
          <MInput value={form.notes} onChange={e => set('notes', e.target.value)}
            placeholder="e.g. Clean down, general shop work..." />
        </MField>
      ) : (
        <>
          <MField label="Project *">
            <MSelect value={form.project_id} onChange={e => set('project_id', e.target.value)}>
              <option value="">Select project...</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.job_number} — {p.title}</option>)}
            </MSelect>
          </MField>
          <ProjectPill project={selectedProject} />
        </>
      )}

      <MField label="Work Centre *">
        <MSelect value={form.work_centre_id} onChange={e => set('work_centre_id', e.target.value)}>
          <option value="">Select work centre...</option>
          {workCentres.map(wc => <option key={wc.id} value={wc.id}>{wc.name}</option>)}
        </MSelect>
      </MField>

      <MField label="Staff Member *">
        <MSelect value={form.staff_id} onChange={e => set('staff_id', e.target.value)}>
          <option value="">Select name...</option>
          {staff.map(s => <option key={s.id} value={s.id}>{s.name}{s.role ? ` — ${s.role}` : ''}</option>)}
        </MSelect>
      </MField>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <MField label="Date *">
          <MInput type="date" value={form.log_date} onChange={e => set('log_date', e.target.value)} />
        </MField>
        <MField label="Hours *">
          <MInput type="number" value={form.hours_decimal} onChange={e => set('hours_decimal', e.target.value)}
            placeholder="e.g. 2.5" min="0.25" max="24" step="0.25" />
        </MField>
      </div>

      {/* % complete — only for productive work */}
      {!form.non_productive && (
        <MField label="% Complete (optional)">
          <MInput type="number" value={form.pct_complete} onChange={e => set('pct_complete', e.target.value)}
            placeholder="0 – 100" min="0" max="100" step="5" />
          {form.pct_complete !== '' && (
            <div style={{ marginTop: 8, height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 3, background: 'var(--accent)', transition: 'width 0.2s ease',
                width: `${Math.min(100, Math.max(0, parseFloat(form.pct_complete) || 0))}%`,
              }} />
            </div>
          )}
        </MField>
      )}

      {/* Batch toggle — only for productive */}
      {!form.non_productive && (
        <Toggle
          label="Batch Entry"
          subLabel="Log covers multiple units at once"
          value={form.is_batch}
          onChange={v => set('is_batch', v)}
          color="var(--accent)"
        />
      )}

      {/* Notes — only shown for productive work (non-prod uses activity field above) */}
      {!form.non_productive && (
        <MField label="Notes (optional)">
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
            placeholder="Any notes about this work..."
            style={{ ...mobileInput, resize: 'vertical', minHeight: 80 }} />
        </MField>
      )}

      <button onClick={submit} disabled={saving} style={{
        width: '100%', padding: '18px 24px', marginTop: 4, border: 'none', borderRadius: 6,
        background: saving ? 'var(--accent-dim)' : 'var(--accent)', color: '#000',
        fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, letterSpacing: 2,
        textTransform: 'uppercase', cursor: saving ? 'not-allowed' : 'pointer', transition: 'background 0.15s',
      }}>
        {saving ? 'Saving...' : '⚙ Log Entry'}
      </button>
    </div>
  );
}

// ─── Log Entry tab (mode switcher + sub-forms) ────────────────────────────────
function LogEntry({ projects, workCentres, staff, onLogged }) {
  const activeProjects = projects.filter(
    p => !['delivered', 'invoiced'].includes(p.status)
  );
  const [mode, setMode] = useState('clock');
  return (
    <div>
      {/* Mode switcher */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, maxWidth: 560 }}>
        <ModeBtn active={mode === 'clock'}  onClick={() => setMode('clock')}>Clock On/Off</ModeBtn>
        <ModeBtn active={mode === 'manual'} onClick={() => setMode('manual')}>Manual Entry</ModeBtn>
      </div>

      {mode === 'clock'
        ? <ClockMode  projects={activeProjects} workCentres={workCentres} staff={staff} onLogged={onLogged} />
        : <ManualMode projects={activeProjects} workCentres={workCentres} staff={staff} onLogged={onLogged} />
      }
    </div>
  );
}

// ─── Recent Logs ──────────────────────────────────────────────────────────────
function RecentLogs({ logs, loading, projects, workCentres, staff, onDelete, onRefresh }) {
  const [projectFilter,    setProjectFilter]    = useState('');
  const [workCentreFilter, setWorkCentreFilter] = useState('');
  const [staffFilter,      setStaffFilter]      = useState('');

  const filtered = logs.filter(l => {
    if (projectFilter    && l.project_id     !== parseInt(projectFilter))    return false;
    if (workCentreFilter && l.work_centre_id !== parseInt(workCentreFilter)) return false;
    if (staffFilter      && l.staff_id       !== parseInt(staffFilter))      return false;
    return true;
  });

  const totalHours = filtered.reduce((s, l) => s + parseFloat(l.hours_decimal || 0), 0);
  const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—';

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20, alignItems: 'center' }}>
        <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)}
          style={{ ...mobileInput, maxWidth: 220, padding: '8px 12px', fontSize: 13 }}>
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.job_number} — {p.title}</option>)}
        </select>
        <select value={workCentreFilter} onChange={e => setWorkCentreFilter(e.target.value)}
          style={{ ...mobileInput, maxWidth: 200, padding: '8px 12px', fontSize: 13 }}>
          <option value="">All Work Centres</option>
          {workCentres.map(wc => <option key={wc.id} value={wc.id}>{wc.name}</option>)}
        </select>
        <select value={staffFilter} onChange={e => setStaffFilter(e.target.value)}
          style={{ ...mobileInput, maxWidth: 180, padding: '8px 12px', fontSize: 13 }}>
          <option value="">All Staff</option>
          {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <button onClick={onRefresh} className="btn btn-secondary btn-sm">Refresh</button>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>
          {filtered.length} entries · {totalHours.toFixed(2)}h
        </span>
      </div>

      {loading ? (
        <div className="loading">Loading logs...</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">No production logs found</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(log => (
            <div key={log.id} style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8,
              padding: '14px 16px', display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px 16px',
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                  {log.non_productive ? (
                    <span style={{ background: 'rgba(88,166,255,0.12)', color: 'var(--info)', border: '1px solid rgba(88,166,255,0.3)', borderRadius: 3, padding: '2px 8px', fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase' }}>
                      NON-PROD
                    </span>
                  ) : (
                    <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)', fontSize: 14, fontWeight: 700 }}>
                      {log.job_number || '—'}
                    </span>
                  )}
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    {log.non_productive ? (log.notes || 'Non-productive time') : log.project_title}
                  </span>
                  {log.is_batch && (
                    <span className="badge" style={{ background: 'rgba(88,166,255,0.12)', color: 'var(--info)', border: '1px solid rgba(88,166,255,0.3)', fontSize: 9 }}>BATCH</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>{log.work_centre_name}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-secondary)' }}>{log.staff_name}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>{fmtDate(log.log_date)}</span>
                </div>
                {!log.non_productive && log.notes && (
                  <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>{log.notes}</div>
                )}
              </div>
              <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, lineHeight: 1 }}>
                  {parseFloat(log.hours_decimal).toFixed(2)}
                  <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', marginLeft: 4 }}>h</span>
                </div>
                {log.pct_complete != null && (
                  <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>
                    {parseFloat(log.pct_complete).toFixed(0)}% done
                  </div>
                )}
                <button onClick={() => onDelete(log.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16, padding: 4, lineHeight: 1 }}
                  title="Delete">×</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Setup ────────────────────────────────────────────────────────────────────
// ─── PIN config (in Setup tab) ───────────────────────────────────────────────
function PinConfigSection() {
  const [sfPin,   setSfPin]   = useState('');
  const [mgmtPin, setMgmtPin] = useState('');
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState(null);

  const save = async () => {
    if (sfPin && !/^\d{4}$/.test(sfPin))     { setMsg({ text: 'Shop floor PIN must be 4 digits', ok: false }); return; }
    if (mgmtPin && !/^\d{4}$/.test(mgmtPin)) { setMsg({ text: 'Management PIN must be 4 digits', ok: false }); return; }
    if (!sfPin && !mgmtPin) { setMsg({ text: 'Enter at least one PIN to update', ok: false }); return; }
    setSaving(true);
    try {
      const body = {};
      if (sfPin)   body.shopfloor_pin  = sfPin;
      if (mgmtPin) body.management_pin = mgmtPin;
      await api.patch('/config/pins', body);
      setMsg({ text: 'PINs updated successfully.', ok: true });
      setSfPin(''); setMgmtPin('');
    } catch (e) {
      setMsg({ text: e.response?.data?.error || e.message, ok: false });
    }
    setSaving(false);
  };

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6, padding: 16 }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>
        Change PINs — leave blank to keep current
      </div>
      {msg && (
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, marginBottom: 12, color: msg.ok ? 'var(--success)' : 'var(--danger)' }}>
          {msg.ok ? '\u2713 ' : '\u2717 '}{msg.text}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <MInput type="tel" value={sfPin} onChange={e => setSfPin(e.target.value)}
          placeholder="New shop floor PIN (4 digits)" maxLength={4} inputMode="numeric"
          pattern="[0-9]*" autoComplete="off" />
        <MInput type="tel" value={mgmtPin} onChange={e => setMgmtPin(e.target.value)}
          placeholder="New management PIN (4 digits)" maxLength={4} inputMode="numeric"
          pattern="[0-9]*" autoComplete="off" />
        <button onClick={save} disabled={saving} className="btn btn-primary">
          {saving ? 'Saving...' : 'Update PINs'}
        </button>
      </div>
    </div>
  );
}

function Setup({ workCentres, staff, onRefresh }) {
  const [toast,       setToast]       = useState(null);
  const [wcForm,      setWcForm]      = useState({ name: '', description: '' });
  const [wcSaving,    setWcSaving]    = useState(false);
  const [staffForm,   setStaffForm]   = useState({ name: '', role: '' });
  const [staffSaving, setStaffSaving] = useState(false);

  const addWorkCentre = async () => {
    if (!wcForm.name.trim()) return;
    setWcSaving(true);
    try {
      await api.post('/work-centres', wcForm);
      setWcForm({ name: '', description: '' });
      setToast({ msg: 'Work centre added.', type: 'success' });
      onRefresh();
    } catch (e) { setToast({ msg: e.response?.data?.error || e.message, type: 'error' }); }
    setWcSaving(false);
  };

  const toggleWc = async (wc) => {
    try { await api.patch(`/work-centres/${wc.id}`, { is_active: !wc.is_active }); onRefresh(); }
    catch (e) { setToast({ msg: e.response?.data?.error || e.message, type: 'error' }); }
  };

  const addStaff = async () => {
    if (!staffForm.name.trim()) return;
    setStaffSaving(true);
    try {
      await api.post('/staff', staffForm);
      setStaffForm({ name: '', role: '' });
      setToast({ msg: 'Staff member added.', type: 'success' });
      onRefresh();
    } catch (e) { setToast({ msg: e.response?.data?.error || e.message, type: 'error' }); }
    setStaffSaving(false);
  };

  const toggleStaff = async (s) => {
    try { await api.patch(`/staff/${s.id}`, { is_active: !s.is_active }); onRefresh(); }
    catch (e) { setToast({ msg: e.response?.data?.error || e.message, type: 'error' }); }
  };

  const listItem = (name, subtext, isActive, onToggle) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 6, padding: '12px 14px', opacity: isActive ? 1 : 0.5,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{name}</div>
        {subtext && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{subtext}</div>}
      </div>
      <button onClick={onToggle} className={`btn btn-sm ${isActive ? 'btn-secondary' : 'btn-primary'}`}>
        {isActive ? 'Deactivate' : 'Activate'}
      </button>
    </div>
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
      <Toast msg={toast?.msg} type={toast?.type} onDismiss={() => setToast(null)} />

      <div>
        <div className="card-header" style={{ marginBottom: 16 }}>Work Centres</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          {workCentres.map(wc => listItem(wc.name, wc.description, wc.is_active, () => toggleWc(wc)))}
        </div>
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6, padding: 16 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>Add Work Centre</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <MInput value={wcForm.name} onChange={e => setWcForm(p => ({ ...p, name: e.target.value }))} placeholder="Name (e.g. Tempering)" />
            <MInput value={wcForm.description} onChange={e => setWcForm(p => ({ ...p, description: e.target.value }))} placeholder="Description (optional)" />
            <button onClick={addWorkCentre} disabled={wcSaving || !wcForm.name.trim()} className="btn btn-primary">+ Add</button>
          </div>
        </div>
      </div>

      <div>
        <div className="card-header" style={{ marginBottom: 16 }}>Staff Members</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          {staff.map(s => listItem(s.name, s.role, s.is_active, () => toggleStaff(s)))}
          {staff.length === 0 && <div className="empty-state" style={{ padding: '24px 0' }}>No staff added yet</div>}
        </div>
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6, padding: 16 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>Add Staff Member</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <MInput value={staffForm.name} onChange={e => setStaffForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" />
            <MInput value={staffForm.role} onChange={e => setStaffForm(p => ({ ...p, role: e.target.value }))} placeholder="Role (e.g. Glazier)" />
            <button onClick={addStaff} disabled={staffSaving || !staffForm.name.trim()} className="btn btn-primary">+ Add</button>
          </div>
        </div>
      </div>

      {/* ── PIN Configuration ─────────────────────────── */}
      <div>
        <div className="card-header" style={{ marginBottom: 16 }}>PIN Configuration</div>
        <PinConfigSection />
      </div>
    </div>
  );
}

// ─── Today's Summary strip ────────────────────────────────────────────────────
function TodaySummary({ logs }) {
  if (!logs.length) return null;
  const totalHours = logs.reduce((s, l) => s + parseFloat(l.hours_decimal || 0), 0);
  const byWc = logs.reduce((acc, l) => {
    acc[l.work_centre_name] = (acc[l.work_centre_name] || 0) + parseFloat(l.hours_decimal || 0);
    return acc;
  }, {});
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--accent)',
      borderRadius: 8, padding: '12px 16px', marginBottom: 28,
      display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center',
    }}>
      <div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase' }}>Today</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--accent)', lineHeight: 1 }}>
          {totalHours.toFixed(2)}<span style={{ fontSize: 13, color: 'var(--text-muted)', marginLeft: 4 }}>h</span>
        </div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
          {logs.length} {logs.length === 1 ? 'entry' : 'entries'}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', flex: 1 }}>
        {Object.entries(byWc).map(([wc, h]) => (
          <div key={wc} style={{ background: 'var(--bg-secondary)', borderRadius: 6, padding: '6px 12px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-muted)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 2 }}>{wc}</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700 }}>{h.toFixed(2)}h</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Production page ─────────────────────────────────────────────────────
export default function Production() {
  const [tab, setTab] = useState('log');

  const [projects,       setProjects]       = useState([]);
  const [workCentres,    setWorkCentres]    = useState([]);
  const [allWorkCentres, setAllWorkCentres] = useState([]);
  const [staff,          setStaff]          = useState([]);
  const [allStaff,       setAllStaff]       = useState([]);
  const [logs,           setLogs]           = useState([]);
  const [todayLogs,      setTodayLogs]      = useState([]);
  const [logsLoading,    setLogsLoading]    = useState(true);

  const loadMeta = useCallback(() => {
    api.get('/projects').then(r => setProjects(r.data.value || []));
    api.get('/work-centres').then(r => {
      const all = r.data.value || [];
      setAllWorkCentres(all);
      setWorkCentres(all.filter(w => w.is_active));
    });
    api.get('/staff').then(r => setStaff(r.data.value || []));
    api.get('/staff/all').then(r => setAllStaff(r.data.value || []));
  }, []);

  const loadLogs = useCallback(() => {
    setLogsLoading(true);
    Promise.all([
      api.get('/production-logs?limit=200'),
      api.get('/production-logs/today'),
    ]).then(([all, today]) => {
      setLogs(all.data.value || []);
      setTodayLogs(today.data.value || []);
      setLogsLoading(false);
    }).catch(() => setLogsLoading(false));
  }, []);

  useEffect(() => { loadMeta(); loadLogs(); }, [loadMeta, loadLogs]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this log entry?')) return;
    await api.delete(`/production-logs/${id}`);
    loadLogs();
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">PRO<span>DUCTION</span></h2>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>
          {new Date().toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
      </div>

      <TodaySummary logs={todayLogs} />

      <div style={{ display: 'flex', gap: 8, marginBottom: 28, flexWrap: 'wrap' }}>
        <TabBtn active={tab === 'log'}    onClick={() => setTab('log')}>⚙ Log Entry</TabBtn>
        <TabBtn active={tab === 'recent'} onClick={() => setTab('recent')}>≡ Logs</TabBtn>
        <TabBtn active={tab === 'setup'}  onClick={() => setTab('setup')}>◈ Setup</TabBtn>
      </div>

      {tab === 'log' && (
        <LogEntry projects={projects} workCentres={workCentres} staff={staff} onLogged={loadLogs} />
      )}
      {tab === 'recent' && (
        <RecentLogs logs={logs} loading={logsLoading} projects={projects}
          workCentres={allWorkCentres} staff={allStaff} onDelete={handleDelete} onRefresh={loadLogs} />
      )}
      {tab === 'setup' && (
        <Setup workCentres={allWorkCentres} staff={allStaff} onRefresh={() => { loadMeta(); loadLogs(); }} />
      )}
    </div>
  );
}
